import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import {
  useGetProjectResumenData,
  useUpdateProjectResumenData,
} from 'lib/react-query/queriesProject'
import { useProject } from 'context/ProyectoContext'
import { getModuloByProjectAndVersion } from 'api/estructuraCostes/modulosJitApi'
import { useVersionModel } from 'hooks/useVersionModel'
import { useTotalMo } from 'hooks/useTotalMo'

// Crear el contexto y provider
const ResumenTablesContext = createContext()

export const ResumenTablesProvider = ({ children }) => {
  const { idProject } = useParams()
  const [numeroDivision, setNumeroDivision] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const { data } = useGetProjectResumenData(idProject)
  const update = useUpdateProjectResumenData(idProject)
  const { currentProject } = useProject()
  const { currentVersion } = useVersionModel()

  const [compraSolar, setCompraSolar] = useState([])
  const [construccion, setConstruccion] = useState([])
  const [costesGenerales, setCostesGenerales] = useState([])
  const [otrosHonorarios, setOtrosHonorarios] = useState([])
  const [isSaveDisabled, setIsSaveDisabled] = useState(true)
  const { totalMo } = useTotalMo()

  // Carga inicial de datos
  useEffect(() => {
    if (data) {
      setCompraSolar(data.proyecto.compraSolar || [])
      setConstruccion(data.proyecto.construccion || [])
      setCostesGenerales(data.proyecto.costesGenerales || [])
      setOtrosHonorarios(data.proyecto.otrosHonorarios || [])
      setNumeroDivision(data.proyecto.numeroDivision || 0)
    }
  }, [data])

  // Guardar después de eliminar
  useEffect(() => {
    if (isDeleting) {
      handleSave()
      setIsDeleting(false)
    }
  }, [isDeleting])

  // Deshabilitar botón si hay campos vacíos
  useEffect(() => {
    const hasIncomplete = arr => arr.some(x => !x.name || !x.value)
    const disable =
      hasIncomplete(compraSolar) ||
      hasIncomplete(construccion) ||
      hasIncomplete(costesGenerales) ||
      hasIncomplete(otrosHonorarios)
    setIsSaveDisabled(disable)
  }, [compraSolar, construccion, costesGenerales, otrosHonorarios])

  const formatNumber = val => {
    if (val == null || val === '') return ''
    return Number(val).toLocaleString('es-ES', { minimumFractionDigits: 2 })
  }

  const handleBlur = () => handleSave()

  const handleRemoveRow = (array, setFunc, idx) => {
    setFunc(array.filter((_, i) => i !== idx))
    setIsDeleting(true)
  }

  const handleSave = () => {
    const payload = {
      numeroDivision,
      compraSolar: compraSolar.map(r => ({ ...r, value: Number(r.value) })),
      construccion: construccion.map(r => ({ ...r, value: Number(r.value) })),
      costesGenerales: costesGenerales.map(r => ({ ...r, value: Number(r.value) })),
      otrosHonorarios: otrosHonorarios.map(r => ({ ...r, value: Number(r.value) })),
    }
    update.mutate(payload, {
      onSuccess: () => toast.success('Datos guardados correctamente.'),
      onError: err => toast.error('Error al guardar: ' + err.message),
    })
  }

  const calculateSum = arr => arr.reduce((sum, x) => sum + Number(x.value || 0), 0)

  // Cálculo de módulos y mano de obra
  const { data: modulos, isFetching } = useQuery(
    ['modulos-articulos', idProject, currentVersion],
    () => getModuloByProjectAndVersion(idProject, currentVersion)
  )
  const totalImportProject = useMemo(() => {
    if (!modulos) return 0
    return modulos.modulos.reduce((acc, m) => acc + Number(m.totalImport), 0)
  }, [modulos, isFetching])

  // Totales por sección
  const totalCompraSolar = calculateSum(compraSolar)
  const totalConstruccion =
    calculateSum(construccion) + totalImportProject + Number(totalMo)
  const totalCostesGenerales = calculateSum(costesGenerales)
  const totalOtrosHonorarios = calculateSum(otrosHonorarios)
  const totalImporte =
    totalCompraSolar +
    totalConstruccion +
    totalCostesGenerales +
    totalOtrosHonorarios

  // Definición de tablas
  const tables = [
    { title: 'Compra Solar', data: compraSolar, setData: setCompraSolar, total: totalCompraSolar },
    {
      title: 'Construcción',
      data: construccion,
      setData: setConstruccion,
      total: totalConstruccion,
      isConstruccion: true,
    },
    {
      title: 'Costes Generales',
      data: costesGenerales,
      setData: setCostesGenerales,
      total: totalCostesGenerales,
    },
    {
      title: 'Otros Honorarios',
      data: otrosHonorarios,
      setData: setOtrosHonorarios,
      total: totalOtrosHonorarios,
    },
  ]

  return (
    <ResumenTablesContext.Provider
      value={{
        numeroDivision,
        setNumeroDivision,
        formatNumber,
        handleBlur,
        handleRemoveRow,
        isSaveDisabled,
        tables,
        totalImportProject,
        totalMo,
        totalImporte,
        currentProject,
        modulos,
        isFetching,
      }}
    >
      {children}
    </ResumenTablesContext.Provider>
  )
}

// Hook para el Context
export const useResumenTables = () => {
  const context = useContext(ResumenTablesContext)
  if (!context) {
    throw new Error('useResumenTables debe usarse dentro de ResumenTablesProvider')
  }
  return context
}
